import { useUpdateDemoMutation } from '@carboncopyco/data';
import {
  EditIcon,
  HomeIcon,
  MoreVerticalOutlineIcon,
  VOutlineIcon,
} from '@carboncopyco/icons';
import type { IDemo, IDemoPage } from '@carboncopyco/types';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Dropdown,
  DropdownItem,
  TextButton,
  TooltipBubble,
} from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { ScreenshotTile } from '../../../GridTiles';
import { usePermissionsContext } from '../../../utils';
import { useDemoPageProps } from '../DemoPageContext';

type DemoScreenTileProps = {
  firstUrl: IDemo['firstUrl'];
  screen: IDemoPage;
  goToEditor: (url?: string) => void;
  goToDemoPreview: (url: string | undefined) => void;
};

export const DemoScreenTile = ({
  firstUrl,
  screen,
  goToEditor,
  goToDemoPreview,
}: DemoScreenTileProps) => {
  const { organizationId, demoId, workspaceId, readOnly } = useDemoPageProps();
  const [isOpen, { toggle: toggleDropdown }] = useBoolean(false);
  const [updateDemo] = useUpdateDemoMutation();
  const { trackEvent } = useAnalytics();

  const { getPermissions } = usePermissionsContext();
  const updateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );
  const location = 'demo_info_page_overview_tab_screens_actions_menu';
  return (
    <ScreenshotTile
      title={screen.title || 'Missing title'}
      image={screen.image}
      overlay={
        <div
          onClick={() => goToDemoPreview(screen.url)}
          className="truncate absolute inset-0 z-45 bg-ds-natural-700-a rounded-md hover:cursor-pointer"
        >
          <div className="h-full flex justify-center items-center truncate gap-2">
            <TextButton
              label="Preview"
              color="text-ds-accent3-100"
            />
          </div>
        </div>
      }
      dropdownOpen={isOpen}
      dropdown={
        !readOnly &&
        updateDemoPermission && (
          <Dropdown
            asChild
            interactive
            open={isOpen}
            onOpenChange={toggleDropdown}
            triggerElement={
              <div>
                <TooltipBubble
                  placement="top"
                  title="Actions"
                >
                  <Button
                    aria-label="demo page tile actions"
                    small
                    dark
                    icon={<MoreVerticalOutlineIcon />}
                    onClick={() =>
                      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_ACTIONS, {
                        location:
                          'demo_info_page_overview_tab_screens_actions_menu',
                      })
                    }
                  />
                </TooltipBubble>
              </div>
            }
          >
            <DropdownItem
              label="Edit"
              onClick={() => {
                trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EDIT, {
                  location,
                });
                goToEditor(screen.url);
              }}
              iconLeft={
                <div className="cursor-pointer">
                  <EditIcon size={20} />
                </div>
              }
            />
            <DropdownItem
              label="Set as start page"
              disabled={firstUrl === screen.url}
              onClick={() => {
                trackEvent(AnalyticsTrackEventName.DEMO_CLICK_SET_START_PAGE, {
                  location,
                });
                return firstUrl !== screen.url
                  ? updateDemo({
                      organizationId,
                      workspaceId,
                      id: String(demoId),
                      firstUrl: screen.url,
                    })
                  : undefined;
              }}
              iconLeft={
                <div
                  className={
                    screen.url === firstUrl ? 'cursor-pointer' : undefined
                  }
                >
                  {firstUrl === screen.url ? (
                    <VOutlineIcon />
                  ) : (
                    <HomeIcon size={20} />
                  )}
                </div>
              }
            />
          </Dropdown>
        )
      }
      icon={
        screen.url === firstUrl && (
          <HomeIcon
            size={18}
            color={ColorHex.natural400}
          />
        )
      }
    />
  );
};
